import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { getEnabledContentLibraries } from '@/lib/firebase';
import { ProgramData, ProgramComponent } from '@/pages/add-program';
import { logger } from '@/lib/logger';

interface Step1CategorisationProps {
  data: ProgramData;
  updateData: (updates: Partial<ProgramData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

interface ContentLibrary {
  id: string;
  name: string;
  module: string;
  enabled: boolean;
  key?: string; // Add key field
}

// Calculate the fixed length based on components
// For each component: endDay = startOnDay + numberOfTasks - 1
// The fixedLength is the maximum endDay across all components
// Returns null if there are no components or no valid tasks
const calculateFixedLength = (components: ProgramComponent[]): number | null => {
  if (components.length === 0) return null;
  
  let maxEndDay = 0;
  let hasValidComponent = false;
  
  for (const comp of components) {
    const startOnDay = comp.startOnDay || 1;
    let taskCount = 0;
    
    switch (comp.componentType) {
      case 'videoSeries':
        taskCount = comp.tasks?.length || 0;
        break;
      case 'dynamicTask':
        taskCount = comp.tasks?.length || 0;
        break;
      case 'repeatingTask':
        taskCount = comp.tasks?.length || 1;
        break;
      case 'steps':
        taskCount = 1; // Steps component counts as 1
        break;
    }
    
    // Only count components that have at least one task
    if (taskCount > 0) {
      hasValidComponent = true;
      // End day = start day + number of tasks - 1
      // If a component starts on day 1 with 5 tasks, it ends on day 5
      const endDay = startOnDay + taskCount - 1;
      maxEndDay = Math.max(maxEndDay, endDay);
    }
  }
  
  // Return null if no components have valid tasks
  return hasValidComponent ? maxEndDay : null;
};

export default function Step1Categorisation({ data, updateData, onValidationChange }: Step1CategorisationProps) {
  const [contentLibraries, setContentLibraries] = useState<ContentLibrary[]>([]);
  const [filteredLibraries, setFilteredLibraries] = useState<ContentLibrary[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  
  // Separate UI state for the switch - tracks user intent, independent of stored value
  // Initialize from whether fixedLength is not null
  const [isFixedLengthChecked, setIsFixedLengthChecked] = useState(data.fixedLength !== null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [userToggledOff, setUserToggledOff] = useState(false);
  const calculatedLength = calculateFixedLength(data.components);

  // Sync switch state only on initial mount (when loading existing program)
  // This prevents overwriting user's explicit toggle when components are empty
  useEffect(() => {
    if (!hasInitialized) {
      setIsFixedLengthChecked(data.fixedLength !== null);
      setHasInitialized(true);
    }
  }, [data.fixedLength, hasInitialized]);

  // Update fixedLength when components change (if switch is checked)
  // Only store a real positive value, otherwise keep existing value
  useEffect(() => {
    // Skip during initial load
    if (!hasInitialized) return;
    
    if (isFixedLengthChecked) {
      const newLength = calculateFixedLength(data.components);
      
      // GUARD: If calculation returns null (no valid components), 
      // DO NOT modify the existing fixedLength - just return early
      // This protects against clearing stored values during hydration
      if (newLength === null) {
        return;
      }
      
      // Only update if the calculated value is positive and different
      if (newLength > 0 && newLength !== data.fixedLength) {
        updateData({ fixedLength: newLength });
      }
    } else {
      // Switch is off, ensure null is stored (only if user explicitly toggled off)
      if (userToggledOff && data.fixedLength !== null) {
        updateData({ fixedLength: null });
        setUserToggledOff(false);
      }
    }
  }, [data.components, isFixedLengthChecked, data.fixedLength, updateData, hasInitialized, userToggledOff]);

  // Load content libraries
  useEffect(() => {
    const loadContentLibraries = async () => {
      setLoading(true);
      try {
        const libraries = await getEnabledContentLibraries() as ContentLibrary[];
        setContentLibraries(libraries);
      } catch (error) {
        logger.error('Error loading content libraries:', error);
      } finally {
        setLoading(false);
      }
    };

    loadContentLibraries();
  }, []);

  // Filter content libraries by selected module
  useEffect(() => {
    if (data.module) {
      const filtered = contentLibraries.filter(lib => lib.module === data.module);
      setFilteredLibraries(filtered);
      
      // Only attempt matching after libraries have been loaded
      if (contentLibraries.length > 0 && data.contentLibrary) {
        const matchedLib = filtered.find(lib => {
          const libraryValue = typeof data.contentLibrary === 'string' 
            ? data.contentLibrary.toLowerCase() 
            : '';
          return (
            lib.id === data.contentLibrary || 
            lib.key === data.contentLibrary || 
            lib.name === data.contentLibrary ||
            (libraryValue && lib.key?.toLowerCase() === libraryValue) ||
            (libraryValue && lib.name?.toLowerCase() === libraryValue)
          );
        });
        
        if (matchedLib) {
          // Ensure both contentLibrary and contentLibraryName are set correctly
          // Use key if available, fallback to name or id
          const correctValue = matchedLib.key || matchedLib.name || matchedLib.id;
          
          if (data.contentLibrary !== correctValue || !data.contentLibraryName) {
            updateData({ 
              contentLibrary: correctValue,
              contentLibraryName: matchedLib.name 
            });
          }
        } else if (initialized) {
          // Only reset if invalid AND we're not in the initial load
          updateData({ contentLibrary: '', contentLibraryName: '' });
        }
      }
      
      setInitialized(true);
    } else {
      setFilteredLibraries([]);
    }
  }, [data.module, contentLibraries, data.contentLibrary, data.contentLibraryName, updateData, initialized]);

  // Validation
  useEffect(() => {
    const isValid = !!data.module && !!data.contentLibrary;
    onValidationChange(isValid);
  }, [data.module, data.contentLibrary, onValidationChange]);

  const handleModuleChange = (value: string) => {
    updateData({ 
      module: value as 'general' | 'mind' | 'body',
      // Reset content library when module changes
      contentLibrary: '',
      contentLibraryName: ''
    });
  };

  const handleContentLibraryChange = (value: string) => {
    // Find library by matching the value (which could be key, name, or id)
    const selectedLib = filteredLibraries.find(lib => 
      lib.id === value || lib.key === value || lib.name === value
    );
    updateData({ 
      contentLibrary: selectedLib?.key || selectedLib?.name || value, // Use key if available, fallback to name or ID
      contentLibraryName: selectedLib?.name || ''
    });
  };

  const handleFixedLengthToggle = (checked: boolean) => {
    // Update UI state - effect will handle data.fixedLength update
    setIsFixedLengthChecked(checked);
    if (!checked) {
      // Mark that user explicitly toggled off
      setUserToggledOff(true);
    }
  };

  return (
    <div className="space-y-6">
      {/* Module */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-700">
          Module <span className="text-red-500">*</span>
        </Label>
        <Select value={data.module} onValueChange={handleModuleChange}>
          <SelectTrigger data-testid="select-module">
            <SelectValue placeholder="Select module" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="general">General</SelectItem>
            <SelectItem value="mind">Mind</SelectItem>
            <SelectItem value="body">Body</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content Library */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-700">
          Content Library <span className="text-red-500">*</span>
        </Label>
        <Select 
          value={data.contentLibrary} 
          onValueChange={handleContentLibraryChange}
          disabled={!data.module || loading || filteredLibraries.length === 0}
        >
          <SelectTrigger data-testid="select-content-library">
            <SelectValue 
              placeholder={
                !data.module 
                  ? "Select a module first" 
                  : loading 
                    ? "Loading libraries..." 
                    : filteredLibraries.length === 0
                      ? "No libraries available for this module"
                      : "Select content library"
              } 
            />
          </SelectTrigger>
          <SelectContent>
            {filteredLibraries.map((library) => (
              <SelectItem 
                key={library.id} 
                value={library.key || library.name || library.id}
              >
                {library.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data.module && filteredLibraries.length === 0 && !loading && (
          <p className="text-sm text-slate-500">
            No content libraries found for the {data.module} module.
          </p>
        )}
      </div>

      {/* Fixed Length Toggle */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium text-slate-700">
              Fixed Length Program
            </Label>
            <p className="text-sm text-slate-500">
              When enabled, the program will have a fixed duration based on the components
            </p>
          </div>
          <Switch
            checked={isFixedLengthChecked}
            onCheckedChange={handleFixedLengthToggle}
            data-testid="switch-fixed-length"
          />
        </div>
        {isFixedLengthChecked && (
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Calculated Length: </span>
              {calculatedLength !== null && calculatedLength > 0 ? (
                <span>{calculatedLength} day{calculatedLength !== 1 ? 's' : ''}</span>
              ) : (
                <span className="text-blue-600 italic">Add components to calculate length</span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Current Selection Summary */}
      {data.module && data.contentLibraryName && (
        <div className="p-4 bg-slate-50 rounded-lg">
          <h4 className="font-medium text-slate-900 mb-2">Current Selection:</h4>
          <div className="space-y-1 text-sm text-slate-600">
            <p><span className="font-medium">Module:</span> {data.module === 'general' ? 'General' : data.module === 'mind' ? 'Mind' : 'Body'}</p>
            <p><span className="font-medium">Library:</span> {data.contentLibraryName}</p>
          </div>
        </div>
      )}
    </div>
  );
}

